import React, { useCallback } from 'react';
import { read, utils } from 'xlsx';
import * as Hangul from 'hangul-js';

import useLoadingStore from 'zustands/useLoadingStore';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

// Assets
import { ReactComponent as MinusCircleIcon } from 'assets/icons/minuscircle.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

// Data
import AddInvitationList from './add-invitation-list-modal/add-invitation-list';

import Button from 'common/button/button';
import FileUpload from 'common/file-upload/file-upload';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import './bulk-registration.scss';
import { useNavigate } from 'react-router-dom';
import { IEmp } from 'types/employee-registration';
import CommonModal from 'common/CommonModal';
import { AxiosResponse } from 'axios';

const make_cols = (refstr: string) => {
  let o = [],
    C = utils.decode_range(refstr).e.c + 1;
  for (var i = 0; i < C; ++i) o[i] = { name: utils.encode_col(i), key: i };
  return o;
};

const BulkRegistration: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [isFileUploaded, setIsFileUploaded] = React.useState<boolean>(false); // 파일 업로드 여부
  const [employeeData, setEmployeeData] = React.useState<IEmp[]>([]); // 추가된 직원 목록 데이터
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [showAddInvitationListModal, setShowAddInvitationListModal] = React.useState<boolean>(false); // 추가입력 모달 표시 여부

  // 엑셀 양식 다운로드
  const downloadExcelForm = () => {
    window.location.href =
      'https://wesop.s3.ap-northeast-2.amazonaws.com/shopsol/%E1%84%89%E1%85%A3%E1%86%B8%E1%84%89%E1%85%A9%E1%86%AF_%E1%84%83%E1%85%A1%E1%86%AB%E1%84%8E%E1%85%A6%E1%84%8E%E1%85%A9%E1%84%83%E1%85%A2%E1%84%92%E1%85%A1%E1%84%80%E1%85%B5%E1%84%8B%E1%85%A3%E1%86%BC%E1%84%89%E1%85%B5%E1%86%A8.xlsx';
  };

  // 엑셀파일 제거 시 상태 초기화
  const resetDisappearFile = () => {
    setIsFileUploaded(false);
    setEmployeeData([]);
    setSearchValue('');
  };

  // 파일 업로드 시 데이터 추출
  const isFilesUploaded = (e: File[]) => {
    const files = e;
    if (!files || files.length === 0) {
      return resetDisappearFile();
    }

    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;
    reader.onload = (e) => {
      const bstr = e.target?.result;
      const wb = read(bstr, {
        type: rABS ? 'binary' : 'array',
        bookVBA: true,
      });
      // get first worksheet
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // Convert array of arrays
      const data: { [key: string]: string }[] = utils.sheet_to_json(ws, { range: 1 });
      // Update state
      if (ws['!ref']) {
        FormatExcelEmps(files[0], data, make_cols(ws['!ref']));
      }
    };

    if (rABS) {
      reader.readAsBinaryString(files[0]);
    } else {
      reader.readAsArrayBuffer(files[0]);
    }
  };

  // 단체 초대하기 엑셀 데이터 가공
  function FormatExcelEmps(file: File, data: { [key: string]: string }[], cols: { name: string; key: number }[]) {
    let missList: { rowIdx: number; content: string }[] = [];
    if (data.length > 0) {
      let list: { [key: string]: string }[] = [];
      data.map((item, index) => {
        let regExp_ctn = /^(01[016789]{1}|02|0[3-9]{1}[0-9]{1})-([0-9]{3,4})-([0-9]{4})$/;
        if (item['직원명'] == '' || item['직원명'] == undefined) {
          missList.push({
            rowIdx: index + 3,
            content: '직원명이 입력되지 않았습니다.',
          });
        } else if (item['휴대폰번호'] == '' || item['휴대폰번호'] == undefined) {
          missList.push({
            rowIdx: index + 3,
            content: '휴대폰번호가 입력되지 않았습니다.',
          });
        } else if (item['직원명'].length > 7) {
          missList.push({
            rowIdx: index + 3,
            content: '직원명은 최대 7자 입니다.',
          });
        } else if (item['휴대폰번호'].length == 0 || item['휴대폰번호'].length == 2) {
          missList.push({
            rowIdx: index + 3,
            content: '휴대폰번호 형식이 올바르지 않습니다.',
          });
        } else if (!regExp_ctn.test(item['휴대폰번호'])) {
          missList.push({
            rowIdx: index + 3,
            content: '휴대폰번호 형식이 올바르지 않습니다.',
          });
        } else {
          list.push({
            name: item['직원명'],
            phone: item['휴대폰번호'],
          });
        }
      });

      if (list.length == 0) {
        setModalState({
          isModalShow: true,
          title: '직원 일괄 초대',
          desc: '선택하신 파일에 유효한 초대 정보가 없습니다. 다운로드 받은 양식을 통하여 직원명과 휴대폰번호를 정확히 입력해 주세요 (직원명 최대 6자리)',
        });
      } else {
        if (missList.length > 0) {
          const missDescription = missList.map((item) => {
            return `행번호: ${item.rowIdx} / ${item.content}`;
          });

          setModalState({
            isModalShow: true,
            title: '직원 일괄 초대',
            desc: `해당 입력값이 올바르지 않습니다.\n\n${missDescription.join('\n')}`,
          });
        }

        setEmployeeData(
          list.map((i) => {
            return {
              empName: i.name,
              empPhone: i.phone,
            };
          }),
        );
        setIsFileUploaded(true);
      }
    } else {
      return setModalState({
        isModalShow: true,
        title: '직원 일괄 초대',
        desc: `선택하신 파일에 유효한 초대 정보가 없습니다. 다운로드 받은 양식을 통하여 직원명과 휴대폰번호를 정확히 입력해 주세요`,
      });
    }
  }

  // 추가된 직원목록 아이템 제거
  const removeInvite = (phone: string) => {
    const empData = employeeData.filter((i) => i.empPhone !== phone);
    setEmployeeData(empData);
  };

  // 추가된 직원목록 검색어 입력
  const handleSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSearchValue(value);
    },
    [searchValue],
  );

  // 검색어에 의한 직원목록 표시 필터링
  const searchFiltering = () => {
    const list = employeeData;
    if (list.length > 0) {
      const search1 = searchValue;
      const search2 = Hangul.disassemble(search1).join('');
      const result = list.filter((item) => {
        return (
          item.empName.includes(search1) || item.empPhone.includes(search1) || item.disassembled?.includes(search2)
        );
      });

      return searchValue === '' ? employeeData : result;
    } else {
      return [];
    }
  };

  // 직원 추가 모달 표시 토글
  const toggleAddInvitation = () => {
    setShowAddInvitationListModal(!showAddInvitationListModal);
  };

  // 추가입력 모달에서 데이터 추가
  const directInputAddEmp = (empName: string, empPhone: string) => {
    const empData = [
      {
        empName,
        empPhone: empPhone,
      },
      ...employeeData,
    ];
    setEmployeeData(empData);
    setModalState({
      isModalShow: true,
      title: '추가완료',
      desc: ``,
    });
  };

  // 직원초대 완료 요청
  const sendInvite = async () => {
    try {
      if (employeeData.length === 0) {
        return setModalState({
          isModalShow: true,
          title: '직원 일괄 초대',
          desc: '추가된 직원이 업습니다. 파일을 업로드해 주세요',
        });
      }
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/inviteEmp`, {
        LIST: employeeData.map((i) => ({ NAME: i.empName, PHONE: i.empPhone.split('-').join('') })),
      });

      if (data.resultCode === 0) {
        setModalState({
          isModalShow: true,
          title: '직원 일괄 초대 완료',
          desc: '초대확인 알림이 오면 [승인대기중] 목록에서 합류를 승인해 주세요. (초대받은 직원이 앱에 로그인 하게 되면 초대확인 알림이 도착합니다)',
        });
        navigate(-1);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/inviteEmp');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const dropdownStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: '#FFFFFF',
      borderColor: '#E9E9E9',
      borderRadius: '8px',
      width: '94px',
      fontFamily: 'Spoqa Han Sans Neo',
      fontWeight: 400,
      minHeight: '34px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  const searchFilterdEmpList = searchFiltering();

  return (
    <>
      <div className="employee-invite__background">
        <PlainNavbar
          backgroundColor="#f5f5f5"
          route="employee-registration"
          customButton={
            <div>
              <Button label="초대문자 보내기" type="type2" height={42} action={sendInvite} />
            </div>
          }>
          <div className="employee-invite">
            <div className="employee-invite__head">
              <div className="employee-invite__head__head-text">직원 일괄 초대</div>
              <div className="employee-invite__head__subhead-text">초대할 직원의 정보를 업로드해 주세요.</div>
            </div>

            <div className="form-cards">
              <div className="employee-invite__card-profile-changes">
                <table className="employee-invite__body__table">
                  <tr>
                    <td className="employee-invite__body__left">
                      <div className="excel-form">
                        <div className="excel-form__head">1.엑셀 양식 다운로드</div>
                        <div className="download">
                          <Button label="엑셀 양식 다운로드" type="type4" action={downloadExcelForm} />
                        </div>
                      </div>
                    </td>
                    <td className="employee-invite__body__right">
                      <div className="info__part1">
                        <div className="info__head">엑셀을 다운로드 받아 사용하세요</div>
                        <div className="info__body">
                          제공된 엑셀의 서식을 변경하거나 다른 파일로 업로드하면 인식이 불가능합니다.
                        </div>
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
            <div className="form-cards">
              <div className="employee-invite__card-profile-changes">
                <table className="employee-invite__body__table">
                  <tr>
                    <td className="employee-invite__body__left">
                      <div className="excel-form">
                        <div className="excel-form__head">2.직원 정보 업로드</div>
                        <div className="download">
                          <FileUpload isFilesUploaded={isFilesUploaded} />
                          {isFileUploaded && (
                            <div className="invitation-list">
                              <div className="invitation-list__head">
                                <div className="invitation-list__head">
                                  <div className="invitation-list__head__heading">초대목록</div>
                                  <div className="invitation-list__head__number">{searchFilterdEmpList.length}</div>
                                </div>
                                <div className="invitation-list__head__buttons">
                                  <div>
                                    <div className="search-icon">
                                      <SearchIcon className="search-icon__icon" />
                                    </div>
                                    <input
                                      type="text"
                                      value={searchValue}
                                      onChange={handleSearchInput}
                                      placeholder="검색어 입력"
                                      className="search-icon__search"
                                    />
                                  </div>
                                  <div>
                                    <Button label="+ 추가" type="type3" action={toggleAddInvitation} height={34} />
                                  </div>
                                </div>
                              </div>
                              <table className="table-reg">
                                <tr className="table-reg__tr">
                                  <th className="employee-invite__table__head">이름*</th>
                                  <th className="employee-invite__table__head">핸드폰 번호*</th>
                                  <th className="employee-invite__table__head-center">삭제</th>
                                </tr>
                                {searchFilterdEmpList.length > 0 &&
                                  searchFilterdEmpList.map((i) => (
                                    <tr className="table-reg__tr" key={`${i.empName}_${i.empPhone}`}>
                                      <td className="employee-invite__table__td-bold">{i.empName}</td>
                                      <td className="employee-invite__table__td">{i.empPhone}</td>
                                      <td
                                        className="employee-invite__table__td-center"
                                        onClick={() => {
                                          removeInvite(i.empPhone);
                                        }}>
                                        <MinusCircleIcon />
                                      </td>
                                    </tr>
                                  ))}
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="employee-invite__body__right">
                      <div className="info__part1">
                        <div className="info__head">엑셀을 다운로드 받아 사용하세요</div>
                        <div className="info__body">
                          제공된 엑셀의 서식을 변경하거나 다른 파일로 업로드하면 인식이 불가능합니다.
                        </div>
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </PlainNavbar>
      </div>
      <CommonModal isVisible={showAddInvitationListModal} onClose={toggleAddInvitation} title="초대목록 추가">
        <AddInvitationList onClose={toggleAddInvitation} onSubmit={directInputAddEmp} />
      </CommonModal>
    </>
  );
};
export default BulkRegistration;
